import { LoginSuccessEvent, trackEvent } from '@otter-ai/telemetry';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { PATH_URL } from '#constants';
import { useAuthNavigation } from '#hooks';
// import { useElectron } from '@/desktop/useElectron';
import { loginInfoQueryCreator, userProfileQueryCreator } from '#query';
import { AuthWorkspace, LoginType } from '#types';
import {
  getPartialLoginState,
  getSessionItem,
  LOCAL_STORAGE_KEYS,
  removeFromLocalStorage,
  SESSION_STORAGE_KEYS,
  setInLocalStorage,
  setPartialLoginState,
} from '#utils';
import { useHandleWorkspaceInvitation } from './useHandleWorkspaceInvitation.ts';

export const useHandleEmailLoginSuccess = ({
  skipRedirect = false,
  skipHandleLoginSuccess = false,
}: {
  skipRedirect?: boolean;
  skipHandleLoginSuccess?: boolean;
}) => {
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();

  const { goToSignedInApp, goTo2FACode } = useAuthNavigation();
  const { acceptInvitationAsync } = useHandleWorkspaceInvitation();
  // const { setIsLoggedInElectron } = useElectron();

  const invalidateAuthQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries(loginInfoQueryCreator()),
      queryClient.invalidateQueries(userProfileQueryCreator()),
    ]);
  };

  const handleAcceptInvitation = async (
    workspace: AuthWorkspace | null,
    userId: string,
  ) => {
    const hasInvitationToken = !!getSessionItem(
      SESSION_STORAGE_KEYS.INVITATION_TOKEN,
    );

    if (!hasInvitationToken || !workspace?.id) {
      return;
    }

    try {
      const acceptInvitationResponse = await acceptInvitationAsync({
        userid: userId,
        workspaceId: workspace.id.toString(),
      });

      if (acceptInvitationResponse?.status === 'OK' && workspace.name) {
        setInLocalStorage(
          LOCAL_STORAGE_KEYS.POST_AUTH_NOTIFICATION_MSG,
          `You have successfully joined the ${workspace.name} team!`,
        );
      }
    } catch (_) {}
  };

  const handleSSORedirect = (workspace: AuthWorkspace | null) => {
    if (
      workspace?.is_pending_member &&
      workspace.sso_enabled &&
      workspace.sso_required
    ) {
      // TODO(hao): handle sso login
      setPartialLoginState({
        redirectUrl: PATH_URL.SSO_LOGIN,
      });
    }
  };

  const cleanupLocalStorage = () => {
    removeFromLocalStorage(LOCAL_STORAGE_KEYS.OTTER_LRU_PUBLIC_SPEECH);
  };

  const handleEmailLoginSuccess = async (res: LoginType) => {
    const twoFactorSettings = res.user.two_factor;
    setPartialLoginState({ twoFactorSettings });

    // If 2FA is required, redirect immediately before invalidating queries
    // to avoid refreshing the checkout session. Queries will refresh when
    // the user returns after completing 2FA.
    if (
      twoFactorSettings?.two_factor_required ||
      twoFactorSettings?.two_factor_type
    ) {
      const redirectUrl: string =
        getPartialLoginState().redirectUrl ||
        searchParams.get('r') ||
        PATH_URL.HOME;
      goTo2FACode({ redirectUrl });
      return;
    }

    await invalidateAuthQueries();
    // setIsLoggedInElectron(true); //set login state for desktop app
    trackEvent(
      LoginSuccessEvent({
        AccountType: 'email',
      }),
    );

    if (!skipHandleLoginSuccess) {
      removeFromLocalStorage(LOCAL_STORAGE_KEYS.OTTER_LRU_PUBLIC_SPEECH);

      const workspace = getSessionItem<AuthWorkspace>(
        SESSION_STORAGE_KEYS.WORKSPACE,
      );

      await handleAcceptInvitation(workspace, res.userid.toString());

      cleanupLocalStorage();

      handleSSORedirect(workspace);

      const couponCode = searchParams.get('code');

      if (couponCode) {
        // TODO(hao): handle coupon code
      }
    }

    goToSignedInApp({
      skipRedirect,
      emailVerifyRequired: res.email_verification_required,
    });
  };

  return { handleEmailLoginSuccess };
};
